import { Api } from '@/modules'
import { PromptRulesResponse } from '@shared/models'
import { Dispatch, SetStateAction, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { BrandRules, FocusState, RulesState, TermsState } from '../types'
import { CONSTANTS } from '../utils/index'

interface UsePromptRulesDataReturn {
  isLoading: boolean
  loadPromptRules: () => void
  excludedDataSources: number[]
}

function createEmptyBrandRules(): BrandRules {
  return {
    social: {
      positiveGeneral: '',
      positiveSpecific: '',
      negativeGeneral: '',
      negativeSpecific: '',
      favorableGeneral: '',
      favorableSpecific: '',
      unfavorableGeneral: '',
      unfavorableSpecific: '',
      neutralRules: '',
    },
    press: {
      positiveGeneral: '',
      positiveSpecific: '',
      negativeGeneral: '',
      negativeSpecific: '',
      favorableGeneral: '',
      favorableSpecific: '',
      unfavorableGeneral: '',
      unfavorableSpecific: '',
      neutralRules: '',
    },
  }
}

// As regras globais (contexto e lógica de classificação) são gravadas duplicadas nas duas
// fontes de dados, mas o backend descarta qualquer grupo cuja fonte esteja marcada como
// excluída da favorabilidade. Numa empresa com redes sociais excluída, as regras de lógica
// só existem em imprensa - por isso a leitura não pode fixar redes sociais, senão o campo
// volta vazio depois do reload mesmo com o dado salvo no banco.
function extractGlobalRuleText(rules: PromptRulesResponse[], ruleType: string): string {
  for (const dataSourceID of [CONSTANTS.SOCIAL_MEDIA_DS_ID, CONSTANTS.PRESS_DS_ID]) {
    const ruleText = rules
      .filter((rule) => rule.ruleType === ruleType && rule.dataSourceID === dataSourceID)
      .sort((first, second) => first.ruleIndex - second.ruleIndex)
      .map((rule) => rule.ruleText)
      .join('; ')

    if (ruleText) return ruleText
  }

  return ''
}

export function usePromptRulesData(
  companyID: string | undefined,
  brandIDs: string[],
  activeBrandId: string | null,
  shouldLoadActiveBrand: boolean,
  onBrandLoaded: (brandId: string) => void,
  setFocusState: (brandId: string, updates: Partial<FocusState[string]>) => void,
  setTermsState: (brandId: string, updates: Partial<TermsState[string]>) => void,
  setRules: Dispatch<SetStateAction<RulesState>>,
): UsePromptRulesDataReturn {
  const [excludedDataSources, setExcludedDataSources] = useState<number[]>([])
  const [pendingRequests, setPendingRequests] = useState(0)
  const loadedBrandIdsRef = useRef<Set<string>>(new Set())
  const loadingBrandIdsRef = useRef<Set<string>>(new Set())

  const [runPromptRulesQuery] = Api.PromiseQuery<{
    rules: PromptRulesResponse[]
    focus: any[]
    terms: any[]
    excludedFavorabilityDataSources: number[]
  }>('prompt-rules')

  const isLoading = pendingRequests > 0

  const appendRule = useCallback((currentText: string, value: string) => {
    return currentText ? `${currentText}; ${value}` : value
  }, [])

  const applyPromptRulesData = useCallback(
    (
      brandId: string,
      data: {
        rules: PromptRulesResponse[]
        focus: any[]
        terms: any[]
        excludedFavorabilityDataSources: number[]
      },
    ) => {
      const socialClientFocus = data.focus
        .filter((focus) => focus.focusCategory === 'CLIENTE' && focus.dataSourceID === CONSTANTS.SOCIAL_MEDIA_DS_ID)
        .map((focus) => ({ id: focus.focus, text: focus.focus }))

      const socialCompetitorFocus = data.focus
        .filter((focus) => focus.focusCategory === 'CONCORRENTE' && focus.dataSourceID === CONSTANTS.SOCIAL_MEDIA_DS_ID)
        .map((focus) => ({ id: focus.focus, text: focus.focus }))

      const pressClientFocus = data.focus
        .filter((focus) => focus.focusCategory === 'CLIENTE' && focus.dataSourceID === CONSTANTS.PRESS_DS_ID)
        .map((focus) => ({ id: focus.focus, text: focus.focus }))

      const pressCompetitorFocus = data.focus
        .filter((focus) => focus.focusCategory === 'CONCORRENTE' && focus.dataSourceID === CONSTANTS.PRESS_DS_ID)
        .map((focus) => ({ id: focus.focus, text: focus.focus }))

      setFocusState(brandId, {
        socialClientFocusTags: socialClientFocus,
        socialCompetitorFocusTags: socialCompetitorFocus,
        pressClientFocusTags: pressClientFocus,
        pressCompetitorFocusTags: pressCompetitorFocus,
      })

      const currentRules = createEmptyBrandRules()

      for (const rule of data.rules) {
        const isSocial = rule.dataSourceID === CONSTANTS.SOCIAL_MEDIA_DS_ID
        const target = isSocial ? currentRules.social : currentRules.press

        switch (rule.ruleType) {
          case 'POSITIVO':
            target.positiveSpecific = appendRule(target.positiveSpecific, rule.ruleText)
            break
          case 'EXTRAPOSITIVO':
            target.positiveGeneral = appendRule(target.positiveGeneral, rule.ruleText)
            break
          case 'NEGATIVO':
            target.negativeSpecific = appendRule(target.negativeSpecific, rule.ruleText)
            break
          case 'EXTRANEGATIVO':
            target.negativeGeneral = appendRule(target.negativeGeneral, rule.ruleText)
            break
          case 'FAVORAVEL':
            target.favorableSpecific = appendRule(target.favorableSpecific, rule.ruleText)
            break
          case 'EXTRAFAVORAVEL':
            target.favorableGeneral = appendRule(target.favorableGeneral, rule.ruleText)
            break
          case 'DESFAVORAVEL':
            target.unfavorableSpecific = appendRule(target.unfavorableSpecific, rule.ruleText)
            break
          case 'EXTRADESFAVORAVEL':
            target.unfavorableGeneral = appendRule(target.unfavorableGeneral, rule.ruleText)
            break
          case 'NEUTRO':
            target.neutralRules = appendRule(target.neutralRules, rule.ruleText)
            break
          default:
            break
        }
      }

      setRules((previousRules) => {
        return {
          ...previousRules,
          brands: {
            ...previousRules.brands,
            [brandId]: currentRules,
          },
          global: {
            ...previousRules.global,
            contextRules: previousRules.global.contextRules || extractGlobalRuleText(data.rules, 'GLOBAL'),
            priorityFirstStage:
              previousRules.global.priorityFirstStage || extractGlobalRuleText(data.rules, 'COMPLEMENT_PROMPT_1'),
            prioritySecondStage:
              previousRules.global.prioritySecondStage || extractGlobalRuleText(data.rules, 'COMPLEMENT_PROMPT_2'),
          },
        }
      })

      const updates: Partial<TermsState[string]> = {
        socialPositivePublishersTags: data.terms
          .filter(
            (term) =>
              term.termType === 'PUBLICADOR' &&
              term.dataSourceID === CONSTANTS.SOCIAL_MEDIA_DS_ID &&
              term.termCategory === 'POSITIVO',
          )
          .map((term) => ({ id: term.term, text: term.term })),
        socialNegativePublishersTags: data.terms
          .filter(
            (term) =>
              term.termType === 'PUBLICADOR' &&
              term.dataSourceID === CONSTANTS.SOCIAL_MEDIA_DS_ID &&
              term.termCategory === 'NEGATIVO',
          )
          .map((term) => ({ id: term.term, text: term.term })),
        socialFavorablePublishersTags: data.terms
          .filter(
            (term) =>
              term.termType === 'PUBLICADOR' &&
              term.dataSourceID === CONSTANTS.SOCIAL_MEDIA_DS_ID &&
              term.termCategory === 'FAVORAVEL',
          )
          .map((term) => ({ id: term.term, text: term.term })),
        socialUnfavorablePublishersTags: data.terms
          .filter(
            (term) =>
              term.termType === 'PUBLICADOR' &&
              term.dataSourceID === CONSTANTS.SOCIAL_MEDIA_DS_ID &&
              term.termCategory === 'DESFAVORAVEL',
          )
          .map((term) => ({ id: term.term, text: term.term })),
        socialPositiveProprietaryTags: data.terms
          .filter(
            (term) =>
              term.termType === 'TERMOS_PROPRIETARIOS' &&
              term.dataSourceID === CONSTANTS.SOCIAL_MEDIA_DS_ID &&
              term.termCategory === 'POSITIVO',
          )
          .map((term) => ({ id: term.term, text: term.term })),
        socialNegativeProprietaryTags: data.terms
          .filter(
            (term) =>
              term.termType === 'TERMOS_PROPRIETARIOS' &&
              term.dataSourceID === CONSTANTS.SOCIAL_MEDIA_DS_ID &&
              term.termCategory === 'NEGATIVO',
          )
          .map((term) => ({ id: term.term, text: term.term })),
        socialFavorableProprietaryTags: data.terms
          .filter(
            (term) =>
              term.termType === 'TERMOS_PROPRIETARIOS' &&
              term.dataSourceID === CONSTANTS.SOCIAL_MEDIA_DS_ID &&
              term.termCategory === 'FAVORAVEL',
          )
          .map((term) => ({ id: term.term, text: term.term })),
        socialUnfavorableProprietaryTags: data.terms
          .filter(
            (term) =>
              term.termType === 'TERMOS_PROPRIETARIOS' &&
              term.dataSourceID === CONSTANTS.SOCIAL_MEDIA_DS_ID &&
              term.termCategory === 'DESFAVORAVEL',
          )
          .map((term) => ({ id: term.term, text: term.term })),
        pressPositiveChannelsTags: data.terms
          .filter(
            (term) =>
              term.termType === 'VEICULO' &&
              term.dataSourceID === CONSTANTS.PRESS_DS_ID &&
              term.termCategory === 'POSITIVO',
          )
          .map((term) => ({ id: term.term, text: term.term })),
        pressNegativeChannelsTags: data.terms
          .filter(
            (term) =>
              term.termType === 'VEICULO' &&
              term.dataSourceID === CONSTANTS.PRESS_DS_ID &&
              term.termCategory === 'NEGATIVO',
          )
          .map((term) => ({ id: term.term, text: term.term })),
        pressFavorableChannelsTags: data.terms
          .filter(
            (term) =>
              term.termType === 'VEICULO' &&
              term.dataSourceID === CONSTANTS.PRESS_DS_ID &&
              term.termCategory === 'FAVORAVEL',
          )
          .map((term) => ({ id: term.term, text: term.term })),
        pressUnfavorableChannelsTags: data.terms
          .filter(
            (term) =>
              term.termType === 'VEICULO' &&
              term.dataSourceID === CONSTANTS.PRESS_DS_ID &&
              term.termCategory === 'DESFAVORAVEL',
          )
          .map((term) => ({ id: term.term, text: term.term })),
        pressPositiveProprietaryTags: data.terms
          .filter(
            (term) =>
              term.termType === 'TERMOS_PROPRIETARIOS' &&
              term.dataSourceID === CONSTANTS.PRESS_DS_ID &&
              term.termCategory === 'POSITIVO',
          )
          .map((term) => ({ id: term.term, text: term.term })),
        pressNegativeProprietaryTags: data.terms
          .filter(
            (term) =>
              term.termType === 'TERMOS_PROPRIETARIOS' &&
              term.dataSourceID === CONSTANTS.PRESS_DS_ID &&
              term.termCategory === 'NEGATIVO',
          )
          .map((term) => ({ id: term.term, text: term.term })),
        pressFavorableProprietaryTags: data.terms
          .filter(
            (term) =>
              term.termType === 'TERMOS_PROPRIETARIOS' &&
              term.dataSourceID === CONSTANTS.PRESS_DS_ID &&
              term.termCategory === 'FAVORAVEL',
          )
          .map((term) => ({ id: term.term, text: term.term })),
        pressUnfavorableProprietaryTags: data.terms
          .filter(
            (term) =>
              term.termType === 'TERMOS_PROPRIETARIOS' &&
              term.dataSourceID === CONSTANTS.PRESS_DS_ID &&
              term.termCategory === 'DESFAVORAVEL',
          )
          .map((term) => ({ id: term.term, text: term.term })),
        socialPositiveFavorablePublishersTags: data.terms
          .filter(
            (term) =>
              term.termType === 'PUBLICADOR' &&
              term.dataSourceID === CONSTANTS.SOCIAL_MEDIA_DS_ID &&
              term.termCategory === 'POSITIVO_FAVORAVEL',
          )
          .map((term) => ({ id: term.term, text: term.term })),
        socialPositiveFavorableProprietaryTags: data.terms
          .filter(
            (term) =>
              term.termType === 'TERMOS_PROPRIETARIOS' &&
              term.dataSourceID === CONSTANTS.SOCIAL_MEDIA_DS_ID &&
              term.termCategory === 'POSITIVO_FAVORAVEL',
          )
          .map((term) => ({ id: term.term, text: term.term })),
        socialNegativeUnfavorablePublishersTags: data.terms
          .filter(
            (term) =>
              term.termType === 'PUBLICADOR' &&
              term.dataSourceID === CONSTANTS.SOCIAL_MEDIA_DS_ID &&
              term.termCategory === 'NEGATIVO_DESFAVORAVEL',
          )
          .map((term) => ({ id: term.term, text: term.term })),
        socialNegativeUnfavorableProprietaryTags: data.terms
          .filter(
            (term) =>
              term.termType === 'TERMOS_PROPRIETARIOS' &&
              term.dataSourceID === CONSTANTS.SOCIAL_MEDIA_DS_ID &&
              term.termCategory === 'NEGATIVO_DESFAVORAVEL',
          )
          .map((term) => ({ id: term.term, text: term.term })),
        pressPositiveFavorableChannelsTags: data.terms
          .filter(
            (term) =>
              term.termType === 'VEICULO' &&
              term.dataSourceID === CONSTANTS.PRESS_DS_ID &&
              term.termCategory === 'POSITIVO_FAVORAVEL',
          )
          .map((term) => ({ id: term.term, text: term.term })),
        pressPositiveFavorableProprietaryTags: data.terms
          .filter(
            (term) =>
              term.termType === 'TERMOS_PROPRIETARIOS' &&
              term.dataSourceID === CONSTANTS.PRESS_DS_ID &&
              term.termCategory === 'POSITIVO_FAVORAVEL',
          )
          .map((term) => ({ id: term.term, text: term.term })),
        pressNegativeUnfavorableChannelsTags: data.terms
          .filter(
            (term) =>
              term.termType === 'VEICULO' &&
              term.dataSourceID === CONSTANTS.PRESS_DS_ID &&
              term.termCategory === 'NEGATIVO_DESFAVORAVEL',
          )
          .map((term) => ({ id: term.term, text: term.term })),
        pressNegativeUnfavorableProprietaryTags: data.terms
          .filter(
            (term) =>
              term.termType === 'TERMOS_PROPRIETARIOS' &&
              term.dataSourceID === CONSTANTS.PRESS_DS_ID &&
              term.termCategory === 'NEGATIVO_DESFAVORAVEL',
          )
          .map((term) => ({ id: term.term, text: term.term })),
      }

      setTermsState(brandId, updates)
      setExcludedDataSources(data.excludedFavorabilityDataSources ?? [])
      loadedBrandIdsRef.current.add(brandId)
      onBrandLoaded(brandId)
    },
    [appendRule, onBrandLoaded, setFocusState, setRules, setTermsState],
  )

  const loadSingleBrand = useCallback(
    async (brandId: string) => {
      if (!companyID || !brandId) {
        return
      }

      if (loadedBrandIdsRef.current.has(brandId) || loadingBrandIdsRef.current.has(brandId)) {
        return
      }

      loadingBrandIdsRef.current.add(brandId)
      setPendingRequests((prev) => prev + 1)

      const [data] = await runPromptRulesQuery(undefined, {
        companyID,
        brandID: brandId,
      })

      if (data) {
        applyPromptRulesData(brandId, data)
      }

      loadingBrandIdsRef.current.delete(brandId)
      setPendingRequests((prev) => Math.max(0, prev - 1))
    },
    [applyPromptRulesData, companyID, runPromptRulesQuery],
  )

  const loadPromptRules = useCallback(() => {
    if (activeBrandId) {
      void loadSingleBrand(activeBrandId)
    }
  }, [activeBrandId, loadSingleBrand])

  useEffect(() => {
    loadedBrandIdsRef.current = new Set()
    loadingBrandIdsRef.current = new Set()
    setExcludedDataSources([])
  }, [companyID])

  const brandsToPreload = useMemo(
    () => brandIDs.filter((brandId) => !loadedBrandIdsRef.current.has(brandId)),
    [brandIDs],
  )

  useEffect(() => {
    if (!companyID || !brandsToPreload.length) {
      return
    }

    for (const brandId of brandsToPreload) {
      void loadSingleBrand(brandId)
    }
  }, [brandsToPreload, companyID, loadSingleBrand])

  useEffect(() => {
    if (shouldLoadActiveBrand && activeBrandId) {
      void loadSingleBrand(activeBrandId)
    }
  }, [activeBrandId, loadSingleBrand, shouldLoadActiveBrand])

  return {
    isLoading,
    loadPromptRules,
    excludedDataSources,
  }
}
